import React, { useState } from 'react';
import { Trash2, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGetManualBlockedDates } from '../hooks/useGetManualBlockedDates';
import { useDeleteManualBlockedDateById } from '../hooks/useDeleteManualBlockedDateById';
import DeleteBlockedDateModal from './DeleteBlockedDateModal';
import LoadingSpinner from './LoadingSpinner';

const ManualBlockedDatesList = ({ propertyId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { data: manualBlockedDates = {}, isLoading } = useGetManualBlockedDates(propertyId, currentPage);
  const { mutate: deleteDate, isPending: isDeleting } = useDeleteManualBlockedDateById();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDateToDelete, setSelectedDateToDelete] = useState(null);

  const dates = manualBlockedDates?.data || [];
  const totalPages = manualBlockedDates?.last_page || 1;

  const formatDateDisplay = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDeleteClick = (dateRecord) => {
    setSelectedDateToDelete(dateRecord);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedDateToDelete) {
      deleteDate(
        {
          dateId: selectedDateToDelete.id,
          propertyId: propertyId,
        },
        {
          onSuccess: () => {
            setIsDeleteModalOpen(false);
            setSelectedDateToDelete(null);
          },
        }
      );
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (dates.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-300 p-6 mt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Calendar className="text-prose-brand" size={24} />
          <h3 className="text-xl font-bold text-prose-brand">Manual Blocked Dates</h3>
        </div>
        <p className="text-gray-500 text-center py-8">
          No dates are manually blocked for this property.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-300 p-6 mt-6">
      {/* Header */}
      <div className="flex items-center space-x-2 mb-6">
        <Calendar className="text-prose-brand" size={24} />
        <h3 className="text-xl font-bold text-prose-brand">Manual Blocked Dates</h3>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="py-8">
          <LoadingSpinner message="Loading blocked dates..." />
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left py-3 px-4 font-semibold text-prose-brand">Start Date</th>
              <th className="text-left py-3 px-4 font-semibold text-prose-brand">End Date</th>
              <th className="text-left py-3 px-4 font-semibold text-prose-brand">Duration</th>
              <th className="text-left py-3 px-4 font-semibold text-prose-brand">Added On</th>
              <th className="text-center py-3 px-4 font-semibold text-prose-brand">Action</th>
            </tr>
          </thead>
          <tbody>
            {dates.length > 0 ? (
              dates.map((dateRecord) => {
              const startDate = new Date(dateRecord.start_date);
              const endDate = new Date(dateRecord.end_date);
              const durationDays = Math.ceil(
                (endDate - startDate) / (1000 * 60 * 60 * 24)
              ) + 1; // +1 to include both start and end date
              const createdDate = new Date(dateRecord.created_at);

              return (
                <tr
                  key={dateRecord.id}
                  className="border-b border-gray-200 hover:bg-gray-50 transition duration-150"
                >
                  <td className="py-3 px-4 text-prose-black font-medium">
                    {formatDateDisplay(dateRecord.start_date)}
                  </td>
                  <td className="py-3 px-4 text-prose-black font-medium">
                    {formatDateDisplay(dateRecord.end_date)}
                  </td>
                  <td className="py-3 px-4 text-prose-black/70">
                    {durationDays} {durationDays === 1 ? 'day' : 'days'}
                  </td>
                  <td className="py-3 px-4 text-prose-black/70">
                    {createdDate.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => handleDeleteClick(dateRecord)}
                      disabled={isDeleting}
                      className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete this blocked date range"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              );
            })
            ) : (
              <tr>
                <td colSpan="5" className="px-4 py-12 sm:py-16">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="p-3 sm:p-4 rounded-full bg-gray-100">
                      <Calendar size={32} className="text-prose-black/40" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm sm:text-base font-semibold text-prose-black/70">No blocked dates</p>
                      <p className="text-xs sm:text-sm text-prose-black/50 mt-1">Block dates to prevent bookings</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-300">
          <div className="text-sm text-prose-black/70">
            Showing {dates.length} of {manualBlockedDates?.total || 0} dates
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="p-2 hover:bg-gray-100 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} className="text-prose-brand" />
            </button>
            <div className="flex items-center px-3 py-2 bg-gray-100 rounded-lg">
              <span className="text-sm font-semibold text-prose-brand">
                Page {currentPage} of {totalPages}
              </span>
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="p-2 hover:bg-gray-100 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} className="text-prose-brand" />
            </button>
          </div>
        </div>
      )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteBlockedDateModal
        isOpen={isDeleteModalOpen}
        dateRange={
          selectedDateToDelete
            ? `Are you sure you want to unblock dates from ${formatDateDisplay(
                selectedDateToDelete.start_date
              )} to ${formatDateDisplay(selectedDateToDelete.end_date)}?`
            : undefined
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setSelectedDateToDelete(null);
        }}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ManualBlockedDatesList;
